import { useState } from 'react';
import { C } from '@ridgewill/brand';
import { adminApi, type AdminUser } from '../lib/api.js';
import { useAction, useAsync } from '../lib/useAsync.js';
import {
  PRIORITY_LABELS,
  PLATFORM_PRODUCT,
  TICKET_PRIORITIES,
  newTicketBody,
  productOptions,
  personName,
  type NewTicketForm,
  type SupportApi,
  type TicketPriority,
} from '../lib/support-api.js';
import { Modal, ModalActions } from '../components/Modal.js';
import { Select } from '../components/Select.js';
import { Button, Field, Note, OutcomeNote, inputStyle } from '../components/ui.js';

/**
 * Staff open a ticket by hand (Jon, 2026-09-27).
 *
 * Mostly for a problem in the platform, where whoever opens it is also the
 * person who wants to hear back, so the requester starts as "Me". It can be
 * another account, found by search, or a typed address for somebody who has
 * none or cannot get in.
 *
 * Two boxes, either or both: the description is the request as the requester
 * sees it (and is in their confirmation email), the note is for staff only.
 * Support stores the description as the requester's message, marked as
 * written by staff, and assigns the ticket to whoever opened it.
 */
export function NewTicket({
  api,
  me,
  onClose,
  onCreated,
}: {
  api: SupportApi;
  me: { id: string; email: string | null } | undefined;
  onClose: () => void;
  onCreated: (reference: string) => void;
}) {
  const [form, setForm] = useState<NewTicketForm>({
    subject: '',
    product: PLATFORM_PRODUCT.value,
    priority: 'NORMAL',
    requesterKind: 'me',
    me: me?.id,
    account: null,
    email: '',
    description: '',
    internalNote: '',
    notify: true,
  });
  const set = <K extends keyof NewTicketForm>(key: K, value: NewTicketForm[K]) =>
    setForm((f) => ({ ...f, [key]: value }));
  const [picked, setPicked] = useState<AdminUser | null>(null);
  const action = useAction();
  const ready = newTicketBody({ ...form, me: me?.id });
  /**
   * From support, which reads them from auth: the same list that decides the
   * prefix a ticket actually gets. While it loads, or if it cannot be had,
   * Platform alone is offered, so the form is never blocked on it.
   */
  const products = useAsync(() => api.products(), []);
  const productChoices =
    products.state.status === 'ready' ? productOptions(products.state.data.products) : [PLATFORM_PRODUCT];

  return (
    <Modal
      title="New ticket"
      subtitle="Assigned to you. Staff see both boxes; the requester sees only the description."
      onClose={onClose}
      width={600}
      footer={
        <ModalActions>
          {'missing' in ready ? <span style={{ color: C.muted, fontSize: '.82rem', marginRight: 'auto' }}>Needs: {ready.missing}</span> : null}
          <Button onClick={onClose}>Cancel</Button>
          <Button
            disabled={action.running || 'missing' in ready}
            onClick={() => {
              if (!('body' in ready)) return;
              let reference = '';
              void action
                .run(async () => {
                  reference = (await api.createTicket(ready.body)).reference;
                  return `Opened ${reference}.`;
                })
                .then((ok) => {
                  if (ok) onCreated(reference);
                });
            }}
          >
            {action.running ? 'Opening…' : 'Open ticket'}
          </Button>
        </ModalActions>
      }
    >
      <OutcomeNote outcome={action.outcome} />

      <Field label="Subject">
        <input value={form.subject} onChange={(e) => set('subject', e.target.value)} style={inputStyle} maxLength={200} />
      </Field>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
        <Field label="Product" hint="Sets the reference's prefix.">
          <Select block label="Product" value={form.product} onChange={(v) => set('product', v)} options={productChoices} />
        </Field>
        <Field label="Priority">
          <Select<TicketPriority>
            block
            label="Priority"
            value={form.priority}
            onChange={(v) => set('priority', v)}
            options={TICKET_PRIORITIES.map((p) => ({ value: p, label: PRIORITY_LABELS[p] }))}
          />
        </Field>
      </div>

      <fieldset style={box}>
        <legend style={legend}>Requester</legend>
        <div style={radios} role="radiogroup" aria-label="Requester">
          <label>
            <input type="radio" checked={form.requesterKind === 'me'} onChange={() => set('requesterKind', 'me')} /> Me
            {me?.email ? <span style={{ color: C.muted }}> ({me.email})</span> : null}
          </label>
          <label>
            <input type="radio" checked={form.requesterKind === 'account'} onChange={() => set('requesterKind', 'account')} /> An account
          </label>
          <label>
            <input type="radio" checked={form.requesterKind === 'email'} onChange={() => set('requesterKind', 'email')} /> An email address
          </label>
        </div>
        {form.requesterKind === 'account' ? (
          <AccountSearch
            picked={picked}
            onPick={(u) => {
              setPicked(u);
              set('account', u ? { id: u.id } : null);
            }}
          />
        ) : null}
        {form.requesterKind === 'email' ? (
          <input
            aria-label="Requester's email address"
            type="email"
            value={form.email}
            onChange={(e) => set('email', e.target.value)}
            placeholder="someone@example.com"
            style={inputStyle}
          />
        ) : null}
        {form.requesterKind === 'email' ? (
          <span style={hint}>Not linked to an account. For someone who has none, or cannot get into theirs.</span>
        ) : null}
      </fieldset>

      <Field label="Description" hint="The request as the requester sees it, in their thread and their confirmation email.">
        <textarea
          value={form.description}
          onChange={(e) => set('description', e.target.value)}
          rows={4}
          style={textarea}
        />
      </Field>
      <Field label="Internal note" hint="Staff only. Never shown or emailed to the requester.">
        <textarea
          value={form.internalNote}
          onChange={(e) => set('internalNote', e.target.value)}
          rows={3}
          style={{ ...textarea, background: C.warnBg }}
        />
      </Field>

      <label style={{ display: 'flex', gap: '.5rem', alignItems: 'center', fontSize: '.88rem' }}>
        <input type="checkbox" checked={form.notify} onChange={(e) => set('notify', e.target.checked)} />
        Send them a confirmation email with the reference
      </label>
    </Modal>
  );
}

/**
 * Find an account by name or address, then pick one.
 *
 * Search on Enter or the button rather than on every keystroke: the users
 * endpoint is a real query, and staff type a whole address before looking.
 * A role that cannot list users gets auth's refusal as text, with the typed
 * address as the way round it.
 */
function AccountSearch({ picked, onPick }: { picked: AdminUser | null; onPick: (u: AdminUser | null) => void }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState<AdminUser[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (picked) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '.6rem', fontSize: '.9rem' }}>
        <strong>{personName(picked) ?? picked.id}</strong>
        {picked.name && picked.email ? <span style={{ color: C.muted }}>{picked.email}</span> : null}
        <Button onClick={() => onPick(null)}>Change</Button>
      </div>
    );
  }

  const search = () => {
    const term = q.trim();
    if (!term) return;
    setBusy(true);
    setError(null);
    adminApi
      .users({ terms: [term], limit: 8, agents: 'exclude' })
      .then((page) => setResults(page.users))
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setBusy(false));
  };

  return (
    <div style={{ display: 'grid', gap: '.5rem' }}>
      <div style={{ display: 'flex', gap: '.5rem' }}>
        <input
          aria-label="Search accounts"
          placeholder="Name or email, then Enter…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              search();
            }
          }}
          style={{ ...inputStyle, flex: 1 }}
        />
        <Button disabled={busy || !q.trim()} onClick={search}>
          {busy ? 'Searching…' : 'Search'}
        </Button>
      </div>
      {error ? <Note tone="danger">{error}</Note> : null}
      {results && results.length === 0 ? <span style={hint}>No account matches. Use an email address instead.</span> : null}
      {results && results.length > 0 ? (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: '.25rem' }}>
          {results.map((u) => (
            <li key={u.id}>
              <button type="button" onClick={() => onPick(u)} style={resultRow} className="rw-press">
                <span style={{ fontWeight: 600 }}>{personName(u) ?? u.id}</span>
                {u.name && u.email ? <span style={{ color: C.muted }}>{u.email}</span> : null}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

const box: React.CSSProperties = {
  border: `1px solid ${C.border}`,
  borderRadius: 8,
  padding: '.6rem .9rem .8rem',
  margin: 0,
  display: 'grid',
  gap: '.6rem',
};
const legend: React.CSSProperties = { fontWeight: 600, color: C.textMid, fontSize: '.85rem', padding: '0 .3rem' };
const radios: React.CSSProperties = { display: 'flex', gap: '1.2rem', flexWrap: 'wrap', fontSize: '.88rem' };
const hint: React.CSSProperties = { color: C.muted, fontSize: '.78rem' };
const textarea: React.CSSProperties = { ...inputStyle, width: '100%', resize: 'vertical', fontFamily: 'inherit' };
const resultRow: React.CSSProperties = {
  display: 'flex',
  gap: '.6rem',
  alignItems: 'baseline',
  width: '100%',
  textAlign: 'left',
  background: 'transparent',
  border: `1px solid ${C.border}`,
  borderRadius: 6,
  padding: '.4rem .6rem',
  color: C.text,
  font: 'inherit',
  fontSize: '.88rem',
  cursor: 'pointer',
};
